import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import { FullPage404 } from '@plato/ui-legacy/components/common/full-page-404';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { cn } from '@plato/ui/util/classes';
import { useBreakpoint } from '@plato/ui/util/use-breakpoint';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CustomerActivityFeed } from '../../../features/activity-feed/components/customer-activity-feed.tsx';
import { useAnalytics } from '../../../features/analytics/hooks/use-analytics.ts';
import { CustomerDetailAnalysis } from '../../../features/customer-detail-new/components/customer-detail-analysis.tsx';
import { CustomerDetailGlance } from '../../../features/customer-detail-new/components/customer-detail-glance.tsx';
import { NewCustomerActivityFeed } from '../../../features/customer-detail-new/components/new-customer-activity-feed.tsx';
import { ActivitiesStatsBar } from '../../../features/customer-detail/components/activities-stats-bar.tsx';
import { CustomerDetailHeader } from '../../../features/customer-detail/components/customer-detail-header.tsx';
import {
    CustomerDetailNavigation,
    CustomerDetailPathOverrideEnum,
} from '../../../features/customer-detail/components/customer-detail-navigation.tsx';
import { InsightSection } from '../../../features/customer-detail/components/insight-section.tsx';
import { TenderWinSection } from '../../../features/customer-detail/components/tender-win-section.tsx';
import { InsightListEmpty } from '../../../features/insights-common/components/insight-list-empty.tsx';
import { useInsightsWithAnalytics } from '../../../features/insights-common/hooks/use-insights-with-analytics.ts';
import { isRiskInsightType } from '../../../features/insights-common/lib/insight-bucket.ts';
import { api } from '../../../utils/api.ts';

const tenderWarmUpPollMs = 5000;

export function useCustomerDetailAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    return usePageAiMeta({
        path: '/customers/:customer_id',
        title: t('navigation_tab__activities', { ns: 'customer-detail' }),
        description:
            "One customer's overview and the default tab after opening it from the list. The header " +
            "carries master data, the current period's {{metric}} against last year and open order " +
            'and quote counts. Below are AI risk and opportunity ' +
            'recommendations and the activity timeline of orders, quotes, visit and call notes, ' +
            'tasks, recommendation outcomes and status changes. Send the user here for anything ' +
            'about one specific customer. The data and analysis tabs branch off from it.',
    });
}

function CustomerDetailActivities(): React.JSX.Element {
    const params = useParams<{ customer_id: string }>();
    const erpCustomerId = params.customer_id ?? '';

    const { data: customerBasics, isLoading: isLoadingBasics } =
        api.platoUser.getDetailCustomer.getBasics.useQuery({
            erpCustomerId,
        });

    const { data: openTransactionSummary } =
        api.platoUser.getDetailCustomer.getOpenTransactionSummary.useQuery({
            erpCustomerId,
        });

    const { defaultMetric, defaultTimeframe, enableNewCustomerDetailView, enableTenderWins } = useAppConfig();

    // Prototype (RETAIN-1390). The first request per server instance warms a 90-day cache, so the
    // query polls until the server reports it ready.
    const { data: tenderWins } = api.platoUser.tenderWins.forCustomer.useQuery(
        { erpCustomerId },
        {
            enabled: enableTenderWins,
            refetchInterval: (query) => (query.state.data?.ready === false ? tenderWarmUpPollMs : false),
        },
    );

    // The stats bar has no metric switcher, so `defaultMetric` maps straight to a billing
    // status instead of seeding a tab.
    const { data: customerFinancialsWithTimeframe, isLoading: isLoadingCustomerFinancials } =
        api.platoUser.getDetailCustomer.getFinancialsWithTimeframe.useQuery({
            erpCustomerId,
            billingStatus:
                defaultMetric === 'Order_Sum' ? SalesMetricBasis.ORDER_SUM : SalesMetricBasis.BILLED,
            timeframe: defaultTimeframe,
        });

    const { insights, sendAnalytics, setInsightVisibility } = useInsightsWithAnalytics({
        customerId: customerBasics?.customerId ?? '',
    });

    const sendEvent = useAnalytics();
    const customerId = customerBasics?.customerId;

    useEffect(() => {
        if (customerId) {
            sendEvent('customer_detail_page_viewed', {
                customer_id: customerId,
            });
        }
    }, [customerId, sendEvent]);

    const { t } = useTranslation('insights');

    // Both sections keep the order from `useInsightsWithAnalytics` (pipeline `rank`, one ranking per
    // customer across all types). Risks used to be re-sorted by € magnitude here, which discarded it.
    const risks = insights.filter((i) => isRiskInsightType(i.insight.insight_type));
    const opportunities = insights.filter((i) => !isRiskInsightType(i.insight.insight_type));

    const timelineRef = useRef<HTMLDivElement>(null);
    const isLg = useBreakpoint('lg');

    const scrollToTimeline = (): void => {
        if (isLg) {
            return;
        }

        timelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const hasNoInsights = risks.length === 0 && opportunities.length === 0;

    const insightSections = hasNoInsights ? (
        <InsightListEmpty />
    ) : (
        <div className="flex flex-col gap-4">
            <InsightSection
                title={t('risks')}
                insights={risks}
                customerId={customerBasics?.customerId ?? ''}
                section="risks"
                sendAnalytics={sendAnalytics}
                setInsightVisibility={setInsightVisibility}
            />
            <InsightSection
                title={t('opportunities')}
                insights={opportunities}
                customerId={customerBasics?.customerId ?? ''}
                section="opportunities"
                sendAnalytics={sendAnalytics}
                setInsightVisibility={setInsightVisibility}
            />
        </div>
    );

    if (isLoadingBasics || isLoadingCustomerFinancials) {
        return <LoadingBlockFullPage className=" mt-32" />;
    }

    if (!customerBasics) {
        return <FullPage404 />;
    }
    return (
        <Page className={cn('bg-wash !px-0', enableNewCustomerDetailView && 'gap-0 pb-0')}>
            <div className="flex flex-col gap-3 bg-white">
                <div className={enableNewCustomerDetailView ? 'px-gutter' : 'px-5 sm:px-4'}>
                    <CustomerDetailHeader customer={customerBasics} />
                </div>
                <div className="border-b border-neutral-200">
                    <div className={enableNewCustomerDetailView ? 'px-gutter' : 'px-5 sm:px-4'}>
                        <CustomerDetailNavigation
                            pathOverride={CustomerDetailPathOverrideEnum.ACTIVITIES}
                            customerId={erpCustomerId}
                        />
                    </div>
                </div>
            </div>
            {enableNewCustomerDetailView ? (
                // Only the content is capped, and inside the gutter; the header stays full width for the Umsatzanalysen tab.
                <div className="px-gutter py-page">
                    <div className="mx-auto flex w-full max-w-7xl flex-col gap-section lg:grid lg:grid-cols-12">
                        <div className="flex flex-col gap-section lg:col-span-5 lg:self-start">
                            <CustomerDetailGlance erpCustomerId={erpCustomerId} />
                            {enableTenderWins && tenderWins && (
                                <TenderWinSection
                                    hits={tenderWins.hits}
                                    customerId={customerBasics.customerId}
                                    customerName={customerBasics.customerName ?? ''}
                                />
                            )}
                            <CustomerDetailAnalysis customerId={erpCustomerId} />
                        </div>
                        <div className="lg:col-span-7 lg:min-h-[calc(100vh-1rem)]">
                            <NewCustomerActivityFeed customerBasics={customerBasics} />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3 px-5 sm:px-4 lg:min-h-0 lg:flex-1">
                    {customerFinancialsWithTimeframe && (
                        <ActivitiesStatsBar
                            customerFinancialsWithTimeframe={customerFinancialsWithTimeframe}
                            openTransactionSummary={openTransactionSummary}
                            onScrollToTimeline={scrollToTimeline}
                        />
                    )}
                    <div className="flex flex-col gap-3 lg:grid lg:grid-cols-12">
                        <div className="flex flex-col gap-3 lg:sticky lg:bottom-0 lg:col-span-4 lg:min-h-[calc(100vh-1rem)] lg:self-end">
                            {enableTenderWins && tenderWins && (
                                <TenderWinSection
                                    hits={tenderWins.hits}
                                    customerId={customerBasics.customerId}
                                    customerName={customerBasics.customerName ?? ''}
                                />
                            )}
                            {insightSections}
                        </div>
                        <div ref={timelineRef} className="rounded-lg bg-white p-3 lg:col-span-8">
                            <CustomerActivityFeed customerBasics={customerBasics} />
                        </div>
                    </div>
                </div>
            )}
        </Page>
    );
}

export default CustomerDetailActivities;
